import { AUTH_CONTAINER_CLASS } from "@/components/auth";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { verificationService } from "@/api/verification/verification.service";
import { extractErrorMessage } from "@/utils/errorUtils";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";

export type TEmailVerificationProps = {
  email?: string;
};

export const EmailVerification = ({ email }: TEmailVerificationProps) => {
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);

  const handleResend = async () => {
    setIsResending(true);
    const { error } = await verificationService.resendEmailVerification();
    setIsResending(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Unable to resend",
        description: await extractErrorMessage(error),
      });
      return;
    }

    toast({
      variant: "success",
      title: "Email resent",
      description: "A new verification link has been sent to your inbox.",
    });
  };

  return (
    <div className={AUTH_CONTAINER_CLASS}>
      <div className="grid gap-6">
        <div className="flex items-start">
          <CheckCircle2 className="h-14 w-14 text-green-600" />
        </div>

        <div className="grid gap-2 text-left">
          <h1 className="text-[28px] font-bold leading-tight tracking-tight text-foreground">Email Verification</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            We've sent a verification link to your email address. Please check your inbox and click the link to verify
            your account and complete registration.
          </p>
        </div>

        <div className="text-left">
          <p className="text-base font-semibold text-foreground">{email}</p>
          <p className="mt-4 text-xs text-muted-foreground">Didn't receive the email?</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => void handleResend()}
            disabled={isResending}
          >
            {isResending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              "Resend verification email"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
